// src/api/whatsappService.js
// WhatsApp Business API service layer
// Handles all WhatsApp-related API calls with Firebase auth token injection

import { getAuth } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * WhatsApp Service
 * Handles all WhatsApp-related API calls with authenticated requests
 */
class WhatsAppService {
  constructor() {
    this.baseUrl = `${API_BASE_URL}/api/whatsapp`;
  }

  /**
   * Get the Firebase ID token for authenticated requests
   * @returns {Promise<string|null>}
   */
  async getAuthToken() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        console.warn('WhatsApp Service: No authenticated user');
        return null;
      }
      return await user.getIdToken();
    } catch (error) {
      console.error('WhatsApp Service: Failed to get auth token:', error);
      return null;
    }
  }

  /**
   * Build headers with auth token
   * @returns {Promise<Object>}
   */
  async getHeaders() {
    const token = await this.getAuthToken();
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  // ============================================================
  // CONFIGURATION METHODS
  // ============================================================

  /**
   * Get WhatsApp config for a salon (safe — no access token returned)
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async getConfig(salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/config/${salonId}?salonId=${salonId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch WhatsApp configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
      throw error;
    }
  }

  /**
   * Save or update WhatsApp config for a salon
   * @param {string} salonId
   * @param {Object} configData
   * @returns {Promise<Object>}
   */
  async saveConfig(salonId, configData) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/config/${salonId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          salonId,
          configData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save WhatsApp configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving WhatsApp config:', error);
      throw error;
    }
  }

  /**
   * Verify WhatsApp Business API configuration by testing credentials with Meta
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async verifyConfiguration(salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/config/verify?salonId=${salonId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to verify configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error verifying WhatsApp configuration:', error);
      throw error;
    }
  }

  // ============================================================
  // TEMPLATE METHODS
  // ============================================================

  /**
   * Get WhatsApp message templates (from cache or Meta API)
   * @param {string} salonId
   * @param {boolean} [refresh=false] - Force refresh from Meta API
   * @returns {Promise<Object>}
   */
  async getMessageTemplates(salonId, refresh = false) {
    try {
      const headers = await this.getHeaders();
      let url = `${this.baseUrl}/templates?salonId=${salonId}`;
      if (refresh) url += '&refresh=true';

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || error.message || 'Failed to fetch templates');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching WhatsApp templates:', error);
      throw error;
    }
  }

  /**
   * Force sync templates from Meta API
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async syncTemplates(salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/templates/sync`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ salonId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to sync templates');
      }

      return await response.json();
    } catch (error) {
      console.error('Error syncing WhatsApp templates:', error);
      throw error;
    }
  }

  // ============================================================
  // MESSAGE SENDING METHODS
  // ============================================================

  /**
   * Send a test WhatsApp message to admin users
   * @param {Object} messageData - The message data including targetRecipients
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async sendTestMessage(messageData, salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messageData,
          salonId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send test message');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending test WhatsApp message:', error);
      throw error;
    }
  }

  /**
   * Send a single WhatsApp message immediately
   * @param {Object} messageData - { phoneNumber, body, templateName?, languageCode?, templateComponents? }
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async sendImmediateMessage(messageData, salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/send`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messageData,
          salonId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send message');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      throw error;
    }
  }

  /**
   * Schedule a WhatsApp campaign (stores for later sending)
   * @param {Object} campaignData - Campaign details
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async scheduleCampaign(campaignData, salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/schedule`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          campaignData,
          salonId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to schedule campaign');
      }

      return await response.json();
    } catch (error) {
      console.error('Error scheduling WhatsApp campaign:', error);
      throw error;
    }
  }

  /**
   * Send a single WhatsApp template message immediately
   * @param {Object} data - { phoneNumber, templateName, languageCode, components }
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async sendTemplateMessage(data, salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/send-template`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...data, salonId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send template message');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending WhatsApp template message:', error);
      throw error;
    }
  }

  /**
   * Fire an event-driven (transactional) template message. The backend resolves every template value from
   * Firestore and applies the salon's whatsapp_<templateId> toggle and the customer's opt-in. Never throws:
   * a failed/skipped send must not block the booking or payment that triggered it.
   * @param {Object} data - { templateId, appointmentId } | { templateId, transactionId } | { templateId, customerId, pointsEarned }
   * @param {string} salonId
   */
  async sendTransactional(data, salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/transactional`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...data, salonId })
      });
      return await response.json();
    } catch (error) {
      console.warn('WhatsApp transactional send failed (non-blocking):', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Execute a campaign — send to all recipients via Meta API
   * @param {string} campaignId
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async sendCampaign(campaignId, salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/send-campaign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          campaignId,
          salonId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send campaign');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending WhatsApp campaign:', error);
      throw error;
    }
  }

  // ============================================================
  // CAMPAIGN MANAGEMENT METHODS
  // ============================================================

  /**
   * Get WhatsApp campaigns for a salon
   * @param {string} salonId
   * @param {Object} [filters] - { status, limit }
   * @returns {Promise<Object>}
   */
  async getCampaigns(salonId, filters = {}) {
    try {
      const headers = await this.getHeaders();
      const queryParams = new URLSearchParams({
        salonId,
        ...filters
      });

      const response = await fetch(`${this.baseUrl}/campaigns?${queryParams}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch campaigns');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching WhatsApp campaigns:', error);
      throw error;
    }
  }

  /**
   * Get a specific campaign by ID
   * @param {string} campaignId
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async getCampaignById(campaignId, salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}?salonId=${salonId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch campaign');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching WhatsApp campaign:', error);
      throw error;
    }
  }

  /**
   * Cancel a scheduled campaign
   * @param {string} campaignId
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async cancelCampaign(campaignId, salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/cancel`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ salonId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to cancel campaign');
      }

      return await response.json();
    } catch (error) {
      console.error('Error cancelling WhatsApp campaign:', error);
      throw error;
    }
  }

  /**
   * Get campaign analytics with delivery status
   * @param {string} campaignId
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async getCampaignAnalytics(campaignId, salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/analytics?salonId=${salonId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch analytics');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching WhatsApp campaign analytics:', error);
      throw error;
    }
  }

  // ============================================================
  // BRANDING METHODS (for super admin)
  // ============================================================

  /**
   * Get WhatsApp branding for a salon
   * @param {string} salonId
   * @returns {Promise<Object>}
   */
  async getBranding(salonId) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/branding/${salonId}?salonId=${salonId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch branding');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching WhatsApp branding:', error);
      throw error;
    }
  }

  /**
   * Save WhatsApp branding for a salon (super admin)
   * @param {string} salonId
   * @param {Object} brandingData - { displayName, businessName, isActive }
   * @returns {Promise<Object>}
   */
  async saveBranding(salonId, brandingData) {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/branding/${salonId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          salonId,
          brandingData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save branding');
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving WhatsApp branding:', error);
      throw error;
    }
  }

  // ============================================================
  // UTILITY METHODS
  // ============================================================

  /**
   * Validate phone number format for WhatsApp
   * @param {string} phoneNumber
   * @returns {Promise<Object>}
   */
  async validatePhoneNumber(phoneNumber) {
    try {
      const response = await fetch(`${this.baseUrl}/validate-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phoneNumber })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to validate phone number');
      }

      return await response.json();
    } catch (error) {
      console.error('Error validating phone number:', error);
      throw error;
    }
  }
}

// Export singleton instance
const whatsappService = new WhatsAppService();
export default whatsappService;

// Also export the class for testing purposes
export { WhatsAppService };
